/**
 * This module should be the one we keep using for google analytics 4 events
 *
 * How to use it: dev should add four data attributes to the element it wants to track:
 *
 * To avoid duplicate global values (parameters that will be fired in all the page type)
 * Check @module NewHomeSource.Analytics.Ga4Configuration
 * This have all the default parameters shared by all events of certain page type.
 * Add data-ga4-global-configuration="PageType" to some element in the DOM.
 * If you want to set a specific configuration to specifc event use data-ga4-configuration="PageType"
 *
 * Event Tracker
 * data-event="true"
 * data-ga4-event-name='my event name'
 * data-ga4-event-parameterName='my parameter value'
 * The most important thing is to start with the data-ga4-event name.
 *
 * Impression
 * data-event='true'
 * data-ga4-impression-event-name='my event name'
 * data-ga4-impression-event-parameterName='my parameter value'
 * The most important thing is to start with the data-ga4-impression-event-name name.
 *
 * On demand tracker
 * const myEvent = {
 *      eventType: "GA4Event",
 *      eventName: 'My Event Name',
 *      params: {
 *        parameterName: "ParameterValue"
 *      }
 *  }
 */
(function ga4Tracker(global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Analytics = global.NewHomeSource.Analytics || {};

  const previousModule = global.NewHomeSource.Analytics.Ga4Tracker;
  const globalConfigurationElement = document.querySelector('[data-ga4-global-configuration]');
  const globalConfiguration = (globalConfigurationElement && globalConfigurationElement.dataset.ga4GlobalConfiguration);
  const ga4ImpressionNameAttribute = 'data-ga4-impression-event-name';
  const configurations = {
    eventTrackerAttribute: 'ga4Event',
    identityTrackerAttribute: 'ga4Identity',
    impressionTrackerAttribute: 'ga4ImpressionEvent',
    shareInfoAttribute: 'ga4Info',
    identityUserDataAttribute: 'ga4IdentityUserData',
    identityCheckboxAttribute: 'ga4IdentityCheckbox',
    identityCheckboxDataAttribute: 'ga4IdentityCheckboxData',
    identityDropdownAttribute: 'ga4IdentityDropdown',
  };

  if (!global.NewHomeSource.Analytics.Ga4Configuration) {
    global.NewHomeSource.Analytics.Ga4Tracker = () => { };
    return global.NewHomeSource.Analytics.Ga4Tracker;
  }

  const ga4Configuration = global.NewHomeSource.Analytics.Ga4Configuration;

  /**
   * @constructs NewHomeSource.Analytics.Ga4Tracker
   */
  function Ga4Tracker(globalValues) {
    configurations.globalValues = globalValues;
  }

  function pushToDataLayer(data) {
    const dataLayer = global.dataLayer;

    if (dataLayer) {
      dataLayer.push(data);
    }
  }

  function googlePush(eventName, params) {
    pushToDataLayer({
      event: 'GA4Event',
      eventName,
      params
    });
  }

  /**
   * @private
   * getAttributes: get the attributes that starts with @var{attribute}
   *
   * @param {DOMStringMap} dataset properties of the object.
   * @param {*} prefix to evaluate the properties we want to retrieve.
   * @param {*} attribute being evaluated.
   * @returns object with the property.
   */
  function getAttributes(dataset, prefix, attribute) {
    if (!attribute.startsWith(prefix)) {
      return;
    }

    const cleanProperty = attribute.replace(prefix, '');
    const name = cleanProperty.charAt(0).toLowerCase() + cleanProperty.slice(1);

    if (!name) {
      return;
    }

    this[name] = dataset[attribute];
  }

  function getParameters(dataset, attribute) {
    const parameters = {};
    Object.keys(dataset)
      .forEach(getAttributes.bind(parameters, dataset, attribute));
    return parameters;
  }

  /**
   * @private getSharedParameters: when some data is duplicated in some specific section and events
   * you can add the data-ga4-info="true" to get the the element that contains the information.
   * For example Search results cards.
   * The properties structure: data-ga4-info-parameterName="Paramater Value"
   *
   * @param {Element} element to track.
   * @returns {object} with the share parameters.
   */
  function getSharedParameters(element) {
    const shareInfoElement = element.closest('[data-ga4-info="true"]');
    if (shareInfoElement) {
      return getParameters(shareInfoElement.dataset, configurations.shareInfoAttribute);
    }

    return {};
  }

  function setCheckboxParamters(element) {
    const parameter = element.dataset[configurations.identityCheckboxAttribute];
    const value = element.dataset[configurations.identityCheckboxDataAttribute];
    if (element.checked) {
      if (this[parameter]) {
        this[parameter] += `, ${value}`;
      } else {
        this[parameter] = value;
      }
    }
  }

  function setUserData(element) {
    this[element.dataset[configurations.identityUserDataAttribute]] = element.value;
  }

  function setDropdownParameters(element) {
    if (element.selectedIndex > 0) {
      this[element.dataset[configurations.identityDropdownAttribute]] = element.options[element.selectedIndex].text;
    }
  }

  function setDateSliderParameters(form) {
    const date = form.querySelector('[data-date]');
    if (!form.dataset.ga4IdentityDateSlider ||
      !date) {
      return;
    }

    if (this[form.dataset.ga4IdentityDateSlider]) {
      this[form.dataset.ga4IdentityDateSlider] += `, ${date.dataset.date}`;
      return;
    }

    this[form.dataset.ga4IdentityDateSlider] = date.dataset.date;
  }

  Ga4Tracker.eventTracker = function () {
    const paramsFromHtml = getParameters(this.dataset, configurations.eventTrackerAttribute);
    delete paramsFromHtml.name;
    const sharedParameters = getSharedParameters(this);
    const globalValues = ga4Configuration.getGlobalConfiguration(this.dataset.ga4Configuration || globalConfiguration);
    const params = Object.assign({}, globalValues, sharedParameters, paramsFromHtml);
    googlePush(this.dataset.ga4EventName, params);
    return true;
  };

  Ga4Tracker.onDemandTracker = function () {
    const globalValues = ga4Configuration.getGlobalConfiguration(this.ga4Configuration || globalConfiguration);
    delete this.eventType;
    const params = Object.assign({}, globalValues, this.params);
    googlePush(this.eventName, params);
    return true;
  };

  Ga4Tracker.identityTracker = function () {
    const caller = this.caller;
    const form = this.form;
    const identityData = {};
    const identityCheckboxElements = form.querySelectorAll('[data-ga4-identity-checkbox]');
    const identityDropdownElements = form.querySelectorAll('[data-ga4-identity-dropdown]');
    const identityElements = form.querySelectorAll('[data-ga4-identity-user-data]');
    const paramsFromHtml = getParameters(caller.dataset, configurations.identityTrackerAttribute);
    const sharedParameters = getSharedParameters(caller);
    const event = paramsFromHtml.eventName;
    const globalValues = ga4Configuration.getGlobalConfiguration(caller.ga4Configuration || globalConfiguration);
    identityElements.forEach(setUserData, identityData);
    identityCheckboxElements.forEach(setCheckboxParamters, identityData);
    identityDropdownElements.forEach(setDropdownParameters, identityData);
    setDateSliderParameters.call(identityData, form);
    const params = Object.assign({}, globalValues, sharedParameters, paramsFromHtml, identityData);
    googlePush(event, params);
    return true;
  };

  /**
   * @public
   * impressionTracker: Tracks the impression events.
   * @returns The answer to close the task on the idle queue.
   */
  Ga4Tracker.impressionTracker = function () {
    const pendingImpressions = [];
    this.forEach((element) => {
      const eventName = element.dataset.ga4ImpressionEventName;
      if (eventName) {
        const paramsFromHtml = getParameters(element.dataset, configurations.impressionTrackerAttribute);
        const globalValues = ga4Configuration.getGlobalConfiguration(element.dataset.ga4Configuration ||
          globalConfiguration);
        const params = Object.assign({}, paramsFromHtml, globalValues);
        googlePush(eventName, params);
        element.removeAttribute('data-ga4-impression-event-name');
      }
    });

    return pendingImpressions.length === 0;
  };

  /**
   * @public
   * impressionTrackerSelector: Gets the main impression tracker attribute.
   * @returns The main impression tracker attribute.
   */
  Ga4Tracker.impressionTrackerSelector = function () {
    return ga4ImpressionNameAttribute;
  };

  Ga4Tracker.eventTrackerValidator = function (dataset) {
    if (dataset.ga4EventName) {
      return true;
    }

    return false;
  };

  Ga4Tracker.onDemandTrackerValidator = function (data) {
    return data && data.eventType === 'GA4Event';
  };

  /**
   * @public
   * identityTrackerValidator: validates if the dataset passed as parameter represents a google analytics 4 identity
   * being tracked.
   */
  Ga4Tracker.identityTrackerValidator = function identityTrackerValidator(dataset) {
    return !!dataset.ga4IdentityEventName;
  };

  /**
   * @public
   * noConflict: Returns control of NewHomeSource.SampleModule to the previous script.
   */
  Ga4Tracker.noConflict = function noConflict() {
    global.NewHomeSource.Analytics.Ga4Tracker = previousModule;
    return Ga4Tracker;
  };

  global.NewHomeSource.Analytics.Ga4Tracker = Ga4Tracker;

  return Ga4Tracker;
}(window));
